import type { AxiosRequestConfig, Method } from 'axios'
import axios from 'axios'
import { ElLoading, ElMessage, ElNotification, type LoadingOptions } from 'element-plus'
import { i18n } from '/@/lang/index'
import { ElMessageBox } from 'element-plus'
import { message } from '/@/utils/message'
import { nextTick } from 'vue'
// import adminBaseRoute from '/@/router/static/adminBase'
import { memberCenterBaseRoutePath } from '/@/router/static/memberCenterBase'
import { useAdminInfo } from '@/store/adminInfo'
import { useConfig } from '@/store/config'
import { SYSTEM_ZINDEX } from '@/store/constant/common'
import { useUserInfo } from '@/store/userInfo'
// import { isAdminApp } from '/@/utils/common'

window.requests = []
window.tokenRefreshing = false
const pendingMap = new Map()
const loadingInstance: LoadingInstance = {
	target: null,
	count: 0,
}

/**
 * 根据运行环境获取基础请求URL
 */
export const getUrl = (): string => {
	const value: string = import.meta.env.VITE_API_BASEURL as string
	return value == 'getCurrentDomain' ? window.location.protocol + '//' + window.location.host : value
}

/**
 * 根据运行环境获取基础请求URL的端口
 */
export const getUrlPort = (): string => {
	const url = getUrl()
	return new URL(url).port
}

/**
 * 创建`Axios`
 * 默认开启`reductDataFormat(简洁响应)`,返回类型为`ApiPromise`
 * 关闭`reductDataFormat`,返回类型则为`AxiosPromise`
 */
function createAxios<Data = any, T = ApiPromise<Data>>(axiosConfig: AxiosRequestConfig, options: Options = {}, loading: LoadingOptions = {}): T {
	const config = useConfig()
	// const adminInfo = useAdminInfo()
	const userInfo = useUserInfo()

	const Axios = axios.create({
		baseURL: getUrl(),
		timeout: 1000 * 10,
		headers: {
			'think-lang': config.lang.defaultLang,
			server: true,
			// 'host-name': import.meta.env.VITE_HOST_NAME || 'host1',
		},
		responseType: 'json',
	})

	// 自定义后台入口
	// if (adminBaseRoute.path != '/admin' && isAdminApp() && /^\/admin\//.test(axiosConfig.url!)) {
	//     axiosConfig.url = axiosConfig.url!.replace(/^\/admin\//, adminBaseRoute.path + '.php/')
	// }

	// 合并默认请求选项
	options = Object.assign(
		{
			cancelDuplicateRequest: true, // 是否开启取消重复请求, 默认为 true
			loading: false, // 是否开启loading层效果, 默认为false
			reductDataFormat: true, // 是否开启简洁的数据结构响应, 默认为true
			showErrorMessage: true, // 是否开启接口错误信息展示,默认为true
			showCodeMessage: true, // 是否开启code不为1时的信息提示, 默认为true
			showSuccessMessage: false, // 是否开启code为1时的信息提示, 默认为false
			autoMessage: true, // 是否开启自动消息提示, 默认为false
			anotherToken: '', // 当前请求使用另外的用户token
		},
		options
	)

	// 请求拦截
	Axios.interceptors.request.use(
		(config) => {
			removePending(config)
			options.cancelDuplicateRequest && addPending(config)
			// 创建loading实例
			if (options.loading) {
				loadingInstance.count++
				if (loadingInstance.count === 1) {
					loadingInstance.target = ElLoading.service(loading)
				}
			}
			// 自动携带token
			if (config.headers) {
				const userToken = options.anotherToken || userInfo.getToken()
				if (userToken) (config.headers as anyObj)['ba-client-token'] = userToken
			}

			return config
		},
		(error) => {
			return Promise.reject(error)
		}
	)

	// 响应拦截
	Axios.interceptors.response.use(
		(response) => {
			removePending(response.config)
			if (response.config.responseType == 'json') {
				if (response.data && response.data.code == 200) {
					if (options.autoMessage) {
						message(response.data.message, {
							type: response.data.success ? 'success' : 'error',
						})
					} else if (options.showSuccessMessage) {
						ElNotification({
							message: response.data.message ? response.data.message : i18n.global.t('axios.Operation successful'),
							type: 'success',
							zIndex: SYSTEM_ZINDEX,
						})
					}
				} else {
					// 鉴权不通过
					console.log(response.data.code)
					if (response.data.code == 403) {
						const whiteList = ['/client/User/refreshToken']
						if (whiteList.includes(response.config.url ?? '')) {
							// 弄到自己的那个catch里面处理
							return Promise.reject(response.data)
						}
						// 先尝试刷新token（动态导入避免与 User.ts 循环依赖）
						if (!window.tokenRefreshing) {
							window.tokenRefreshing = true
							return import('/@/api/User')
								.then((m) => m.refreshToken())
								.then((res) => {
									// console.log('refreshToken成功', res)
									userInfo.setToken(res.data.token, 'auth')
									userInfo.setToken(res.data.refresh_token, 'refresh')
									window.requests.forEach((cb) => cb(res.data.token, 'user-refresh'))
									window.requests = []
									return Axios(response.config)
								})
								.catch((err) => {
									ElMessageBox.alert('登录信息已失效，请重新登录', '系统提示', {
										type: 'error',
									}).finally(() => {
										userInfo.removeToken()
										import('/@/router/index').then((m) => m.default.push({ path: '/user/login' }))
										return Promise.reject(err)
									})
								})
								.finally(() => {
									window.tokenRefreshing = false
								})
						} else {
							return new Promise((resolve) => {
								// 用函数形式将 resolve 存入，等待刷新后再执行
								window.requests.push((token: string, type: string) => {
									if (type == 'admin-refresh') {
										response.headers.batoken = `${token}`
									} else {
										response.headers['ba-client-token'] = `${token}`
									}
									resolve(Axios(response.config))
								})
							})
						}
						// const whiteList = ['/client/User/refreshToken']
						// if (whiteList.includes(response.config.url ?? '')) {
						// 	return Promise.reject(response.data)
						// }
					}
					// if (response.data.code == 403 && response.data.extra == 'token_time_out') {
					// 	// 如果是刷新token，弹出提示框，清楚用户信息，返回登录页
					// 	const whiteList = ['/client/User/refreshToken']
					// 	if (whiteList.includes(response.config.url ?? '')) {
					// 		return Promise.reject(response.data)
					// 	}
					// 	if (!window.tokenRefreshing) {
					// 		window.tokenRefreshing = true
					// 		return refreshToken()
					// 			.then((res) => {
					// 				console.log(response.config.url)
					// 				console.log('refreshToken成功', res)
					// 				// console.log('refreshToken成功', res)
					// 				userInfo.setToken(res.data.accessToken, 'auth')
					// 				userInfo.setToken(res.data.refreshToken, 'refresh')
					// 				response.headers['ba-client-token'] = `${res.data.accessToken}`
					// 				window.requests.forEach((cb) => cb(res.data.accessToken, 'user-refresh'))
					// 				window.requests = []
					// 				return Axios(response.config)
					// 			})
					// 			.catch((err) => {
					// 				return Promise.reject(err)
					// 			})
					// 			.finally(() => {
					// 				window.tokenRefreshing = false
					// 			})
					// 	} else {
					// 		return new Promise((resolve) => {
					// 			// 用函数形式将 resolve 存入，等待刷新后再执行
					// 			window.requests.push((token: string, type: string) => {
					// 				if (type == 'admin-refresh') {
					// 					response.headers.batoken = `${token}`
					// 				} else {
					// 					response.headers['ba-client-token'] = `${token}`
					// 				}
					// 				resolve(Axios(response.config))
					// 			})
					// 		})
					// 	}
					// }
					// console.log(options)
					// 实名认证不达标：交给全局对话框统一拦截（动态导入避免循环依赖）
					if (response.data.extra === 'name_verified') {
						import('/@/utils/nameVerifiedDialog').then((m) => m.showNameVerifiedDialog(response.data.message))
						// 清空 message，让形如 `if (res.message) message(res.message)` 的 caller 自动跳过避免重复 toast
						response.data.message = ''
						return Promise.resolve(response.data)
					}
					// 收银台支付方式限制：不 toast，由收银台页面弹窗并回退原支付方式
					if (response.data.extra === 'pay_need_login' || response.data.extra === 'pay_need_name_verified') {
						return Promise.resolve(response.data)
					}
					if (options.autoMessage) {
						message(response.data.message, {
							type: 'error',
						})
					} else if (options.showCodeMessage) {
						ElNotification({ type: 'error', message: response.data.message, zIndex: SYSTEM_ZINDEX })
					}
					// 自动跳转到路由name或path（动态导入避免循环依赖）
					if (response.data.code == 302) {
						import('/@/router/index').then((m) =>
							m.default.push({ path: response.data.data.routePath ?? '', name: response.data.data.routeName ?? '' })
						)
					}
					if (response.data.code == 303) {
						let routerPath = memberCenterBaseRoutePath
						if (response.data.data.type == 'need login') {
							userInfo.removeToken()
							routerPath += '/login'
						}
						import('/@/router/index').then((m) => m.default.push({ path: routerPath }))
					}
					// console.log(response.data)
					// code不等于200, 页面then内的具体逻辑就不执行了
					return Promise.resolve(response.data)
				}
			}

			return options.reductDataFormat ? response.data : response
		},
		(error) => {
			// console.log('error')
			// console.log(error)
			error.config && removePending(error.config)
			options.loading && closeLoading(options) // 关闭loading
			options.showErrorMessage && httpErrorStatusHandle(error) // 处理错误状态码
			return Promise.resolve(error) // 错误继续返回给到具体页面
		}
	)
	return Axios(axiosConfig) as T
}

export default createAxios

/**
 * 处理异常
 * @param {*} error
 */
function httpErrorStatusHandle(error: any) {
	// 处理被取消的请求
	if (axios.isCancel(error)) {
		return console.error(i18n.global.t('axios.Automatic cancellation due to duplicate request:') + error.message)
	}
	let msg = ''
	if (error && error.response) {
		switch (error.response.status) {
			case 302:
				msg = i18n.global.t('axios.Interface redirected!')
				break
			case 400:
				msg = i18n.global.t('axios.Incorrect parameter!')
				break
			case 401:
				msg = i18n.global.t('axios.You do not have permission to operate!')
				break
			case 403:
				msg = i18n.global.t('axios.You do not have permission to operate!')
				break
			case 404:
				msg = i18n.global.t('axios.Error requesting address:') + error.response.config.url
				break
			case 408:
				msg = i18n.global.t('axios.Request timed out!')
				break
			case 409:
				msg = i18n.global.t('axios.The same data already exists in the system!')
				break
			case 500:
				msg = i18n.global.t('axios.服务器内部错误！')
				break
			case 501:
				msg = i18n.global.t('axios.Service not implemented!')
				break
			case 502:
				msg = i18n.global.t('axios.Gateway error!')
				break
			case 503:
				msg = i18n.global.t('axios.Service unavailable!')
				break
			case 504:
				msg = i18n.global.t('axios.The service is temporarily unavailable Please try again later!')
				break
			case 505:
				msg = i18n.global.t('axios.HTTP version is not supported!')
				break
			default:
				msg = i18n.global.t('axios.Abnormal problem, please contact the website administrator!')
				break
		}
	}
	if (error.message.includes('timeout')) msg = i18n.global.t('axios.Network request timeout!')
	if (error.message.includes('Network'))
		msg = window.navigator.onLine ? i18n.global.t('axios.Server exception!') : i18n.global.t('axios.You are disconnected!')
	message(msg, { type: 'error' })
}

/**
 * 关闭Loading层实例
 */
function closeLoading(options: Options) {
	if (options.loading && loadingInstance.count > 0) loadingInstance.count--
	if (loadingInstance.count === 0) {
		loadingInstance.target.close()
		loadingInstance.target = null
	}
}

/**
 * 储存每个请求的唯一cancel回调, 以此为标识
 */
function addPending(config: AxiosRequestConfig) {
	const pendingKey = getPendingKey(config)
	config.cancelToken =
		config.cancelToken ||
		new axios.CancelToken((cancel) => {
			if (!pendingMap.has(pendingKey)) {
				pendingMap.set(pendingKey, cancel)
			}
		})
}

/**
 * 删除重复的请求
 */
function removePending(config: AxiosRequestConfig) {
	const pendingKey = getPendingKey(config)
	if (pendingMap.has(pendingKey)) {
		const cancelToken = pendingMap.get(pendingKey)
		cancelToken(pendingKey)
		pendingMap.delete(pendingKey)
	}
}

/**
 * 生成每个请求的唯一key
 */
function getPendingKey(config: AxiosRequestConfig) {
	let { data } = config
	const { url, method, params, headers } = config
	if (typeof data === 'string') data = JSON.parse(data) // response里面返回的config.data是个字符串对象
	return [
		url,
		method,
		headers && (headers as anyObj).batoken ? (headers as anyObj).batoken : '',
		headers && (headers as anyObj)['ba-client-token'] ? (headers as anyObj)['ba-client-token'] : '',
		JSON.stringify(params),
		JSON.stringify(data),
	].join('&')
}

/**
 * 根据请求方法组装请求数据/参数
 */
export function requestPayload(method: Method, data: anyObj) {
	if (method == 'GET') {
		return {
			params: data,
		}
	} else if (method == 'POST') {
		return {
			data: data,
		}
	}
}

interface LoadingInstance {
	target: any
	count: number
}
interface Options {
	// 是否开启取消重复请求, 默认为 true
	cancelDuplicateRequest?: boolean
	// 是否开启loading层效果, 默认为false
	loading?: boolean
	// 是否开启简洁的数据结构响应, 默认为true
	reductDataFormat?: boolean
	// 是否开启接口错误信息展示,默认为true
	showErrorMessage?: boolean
	// 是否开启code不为1时的信息提示, 默认为true
	showCodeMessage?: boolean
	// 是否开启code为1时的信息提示, 默认为false
	showSuccessMessage?: boolean
	// 当前请求使用另外的用户token
	anotherToken?: string
	// 是否开启自动消息提示, 默认为true
	autoMessage?: boolean
}

/*
 * 感谢掘金@橙某人提供的思路和分享
 * 本axios封装详细解释请参考：https://juejin.cn/post/6968630178163458084?share_token=7831c9e0-bea0-469e-8028-b587e13681a8#heading-27
 */
